import { cn } from "@v1/ui/utils";
import type { ReactElement, ReactNode } from "react";

// i18n-exempt: labels authors type into article markdown, not UI copy
const WARNING_LABELS = ["Achtung:", "Warning:"] as const;

interface HastNode {
  readonly type?: string;
  readonly tagName?: string;
  readonly value?: string;
  readonly children?: readonly HastNode[];
}

function isHastNode(value: unknown): value is HastNode {
  return typeof value === "object" && value !== null;
}

/** Text of the leading `**Label:**` inside a quote, if any. */
function leadingStrongText(node: unknown): string | null {
  if (!isHastNode(node)) return null;
  const paragraph = node.children?.find((child) => child.type === "element");
  const strong = paragraph?.children?.find((child) => child.type === "element");
  if (strong?.tagName !== "strong") return null;
  return strong.children?.map((child) => child.value ?? "").join("") ?? null;
}

/** `> **Gut zu wissen:** …` renders as a note box, `> **Achtung:** …` as a warning box. */
export function HelpCallout({
  node,
  children,
}: {
  readonly node?: unknown;
  readonly children?: ReactNode;
}): ReactElement {
  const label = leadingStrongText(node)?.trim() ?? "";
  const warning = WARNING_LABELS.some((candidate) => label === candidate);
  return (
    <aside
      className={cn(
        "my-5 rounded-lg border px-4 py-3 text-[14px] leading-relaxed [&_p:last-child]:!mb-0",
        warning
          ? "border-amber-300/70 bg-amber-50 text-amber-950/80 [&_strong]:text-amber-900 dark:border-amber-500/30 dark:bg-amber-950/30 dark:text-amber-50/80 dark:[&_strong]:text-amber-100"
          : "border-primary/10 bg-primary/[0.03] text-primary/80 [&_strong]:text-primary/90",
      )}
    >
      {children}
    </aside>
  );
}
